import { Journey } from '@audibene/ta.utils-opportunity'
import { useAuth, useCountry } from 'hooks'
import { useMemo } from 'react'
import { AppointmentResourceConfigurationQueryBy } from '../../../../../constants'
import { Role } from '../../../../../constants/role'
import { AppointmentResourceConfiguration, Resource } from '../../../../../types'
import { groupBy, prop, uniqBy } from '../../../../../util'
import { useResources } from '../../useResources'

type Props = Partial<{
  isActive?: boolean
  resourceConfigurations?: AppointmentResourceConfiguration[]
  shopIds?: string[]
  states?: string[]
}> & {
  journey: Journey
}

interface UseHookContext {
  isError: boolean
  isFetching: boolean
  isLoading: boolean
  resources: Resource[]
}

const useResourcesByResourceConfigurations = ({ journey, isActive, resourceConfigurations = [], shopIds = [] }: Props): UseHookContext => {
  const country = useCountry()
  const queryByToConfigurationsMap = groupBy(resourceConfigurations, prop('query_by'))

  const { user } = useAuth()

  const isExternalFitter = Boolean(
    user?.roles?.includes(Role.EXTERNAL_TA_FITTER) ||
      user?.roles?.includes(Role.PARTNER_FITTER) ||
      user?.roles?.includes(Role.EXTERNAL_NETWORK_FITTER)
  )

  const requests = Object.values(AppointmentResourceConfigurationQueryBy).map((queryBy) => {
    const configurations = queryByToConfigurationsMap[queryBy]
    const enabled = Boolean(configurations)
    const types = configurations?.map(prop('resource_type')) ?? []
    let params = {}

    switch (true) {
      case queryBy === AppointmentResourceConfigurationQueryBy.COUNTRY: {
        params = { countries: [country], isActive, types }
        break
      }
      case queryBy === AppointmentResourceConfigurationQueryBy.SHOP: {
        params = { isActive, shopIds, types }
        break
      }
      default:
        break
    }

    const actualParams = { ...params, journey }

    const isInternalFittersDisabled = types[0] === 'FITTER_INTERNAL' && isExternalFitter
    const isEnabled = enabled && !isInternalFittersDisabled

    return useResources(actualParams, { enabled: isEnabled })
  })

  const isError = requests.some((r) => r.isError)
  const isFetching = requests.some((r) => r.isFetching)
  const isLoading = requests.some((r) => r.isLoading)

  // Stable fingerprint of query data freshness — only changes when a query fetches new data.
  // Used as a useMemo dependency instead of `requests` (which is a new array every render).
  const dataKey = requests.map((r) => r.dataUpdatedAt).join()
  const resources = useMemo(() => {
    const all = requests.flatMap((r) => (r.isError || r.isLoading || !r.data ? [] : r.data.data))
    return uniqBy(all, prop('id'))
  }, [dataKey])

  return { isError, isFetching, isLoading, resources }
}

export { useResourcesByResourceConfigurations }
